import { useEffect } from "react";
import { signOut } from "@/lib/session";
import { safeStorage } from "@/shared/lib/safeStorage";

const SESSION_KEY = "paygate_session";
const TIMEOUT_MS = 2 * 60 * 60 * 1000; // 2 hours

export type LocalSession = {
  sessionId: string;
  email: string;
  lastActive: number;
};

export function createLocalSession(email: string) {
  const session: LocalSession = {
    sessionId:
      typeof crypto !== "undefined" && crypto.randomUUID ? crypto.randomUUID() : String(Date.now()),
    email,
    lastActive: Date.now(),
  };
  safeStorage.setItem(SESSION_KEY, JSON.stringify(session));
  // also broadcast via storage event (other tabs will get it automatically)
  try {
    window.dispatchEvent(
      new StorageEvent("storage", {
        key: SESSION_KEY,
        newValue: JSON.stringify(session),
      })
    );
  } catch {
    /* best-effort cross-tab broadcast */
  }
  return session;
}

export function clearLocalSession() {
  safeStorage.removeItem(SESSION_KEY);
  try {
    window.dispatchEvent(new StorageEvent("storage", { key: SESSION_KEY, newValue: null }));
  } catch {
    /* best-effort cross-tab broadcast */
  }
}

export function getLocalSession(): LocalSession | null {
  const raw = safeStorage.getItem(SESSION_KEY);
  if (!raw) return null;
  try {
    return JSON.parse(raw) as LocalSession;
  } catch {
    return null;
  }
}

/**
 * Hook: sync across tabs + idle auto-logout
 * Use this at top-level protected areas (e.g. in ProtectedRoute or app/layout).
 */
export function useLocalSessionGuard(onForcedLogout?: (reason?: string) => void) {
  useEffect(() => {
    let lastSessionId: string | null = getLocalSession()?.sessionId ?? null;

    const checkExpiration = () => {
      const s = getLocalSession();
      if (!s) {
        onForcedLogout?.("no_session");
        signOut({ callbackUrl: "/login?reason=nosession" });
        return;
      }
      if (Date.now() - s.lastActive > TIMEOUT_MS) {
        clearLocalSession();
        onForcedLogout?.("idle");
        signOut({ callbackUrl: "/login?reason=idle" });
      }
      lastSessionId = s.sessionId; // update tracker
    };

    const onStorage = (e: StorageEvent) => {
      if (e.key !== SESSION_KEY) return;

      const current = getLocalSession();
      if (!current) {
        onForcedLogout?.("other_tab_logged_out");
        signOut({ callbackUrl: "/login?reason=other_tab" });
        return;
      }
      if (current.sessionId !== lastSessionId) {
        onForcedLogout?.("other_tab_logged_in");
        signOut({ callbackUrl: "/login?reason=other_tab" });
      }
    };

    // activity resets lastActive
    const resetActivity = () => {
      const s = getLocalSession();
      if (s) {
        s.lastActive = Date.now();
        safeStorage.setItem(SESSION_KEY, JSON.stringify(s));
      }
    };

    window.addEventListener("storage", onStorage);
    ["mousemove", "keydown", "scroll", "touchstart", "click"].forEach((evt) =>
      window.addEventListener(evt, resetActivity)
    );
    const interval = setInterval(checkExpiration, 60_000);
    checkExpiration();

    return () => {
      window.removeEventListener("storage", onStorage);
      ["mousemove", "keydown", "scroll", "touchstart", "click"].forEach((evt) =>
        window.removeEventListener(evt, resetActivity)
      );
      clearInterval(interval);
    };
  }, [onForcedLogout]);
}
